import React, { useCallback, useEffect, useState } from 'react';
import { History } from 'lucide-react';
import Modal from '../ui/Modal';
import { listTextFieldVersions, restoreTextFieldVersion } from '../../api/textFieldVersions';
import { formatDate, truncateText } from '../../pages/PromptTemplates/constants';

const SOURCE_LABELS = {
  initial: 'Inicial',
  manual_save: 'Salvo manualmente',
  ai_improve: 'Antes de melhoria IA',
  rollback: 'Restaurado',
};

function TextVersionHistory({
  resourceType,
  resourceId,
  fieldName,
  onRestore,
  disabled = false,
  buttonClassName = 'inline-flex items-center gap-1.5 text-xs text-forge-600 hover:text-forge-700 disabled:opacity-40 disabled:cursor-not-allowed',
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [restoringId, setRestoringId] = useState(null);

  const loadVersions = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await listTextFieldVersions({
        resourceType,
        resourceId,
        fieldName,
      });
      setVersions(response.data || []);
    } catch (error) {
      setLoadError('Não foi possível carregar o histórico.');
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [resourceType, resourceId, fieldName]);

  useEffect(() => {
    if (open) {
      loadVersions();
    }
  }, [open, loadVersions]);

  const handleRestore = async (version) => {
    setRestoringId(version.id);
    setLoadError('');
    try {
      const result = await restoreTextFieldVersion(version.id);
      onRestore(result.content);
      setOpen(false);
    } catch (error) {
      setLoadError('Não foi possível restaurar esta versão.');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={buttonClassName}
        title="Ver histórico de versões"
      >
        <History className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Histórico
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Histórico de versões" panelClassName="max-w-2xl">
        {isLoading ? (
          <p className="text-sm text-content-muted py-6 text-center">Carregando histórico...</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-content-muted py-6 text-center">Nenhuma versão salva ainda.</p>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto">
            {versions.map((version) => (
              <li
                key={version.id}
                className="rounded-xl border border-surface-border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-content-primary">
                    v{version.version_number} · {SOURCE_LABELS[version.source] || version.source}
                  </p>
                  <p className="text-xs text-content-muted mt-0.5">{formatDate(version.created_at)}</p>
                  <p className="text-xs text-content-secondary mt-2">
                    {truncateText(version.content, 120)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRestore(version)}
                  disabled={Boolean(restoringId)}
                  className="btn-secondary text-sm px-3 py-2 shrink-0"
                >
                  {restoringId === version.id ? 'Restaurando...' : 'Restaurar'}
                </button>
              </li>
            ))}
          </ul>
        )}
        {loadError && <p className="mt-3 text-sm text-red-600">{loadError}</p>}
      </Modal>
    </>
  );
}

export default TextVersionHistory;
